// @ts-strict-ignore
import { UndoAttachArticlesOperation } from './undo-attach-articles-operation';
import { UndoDetachArticlesOperation } from './undo-detach-articles-operation';
import { UndoMoveArticlesOperation } from './undo-move-articles-operation';
import { ArticleUsageVO } from '@article/model/vo/article.vo';
import { UndoReplaceArticleOperation } from './undo-replace-article-operation';
import { Connection } from '@3d-scene/model3d/connection';
import { BaseArticlesSequenceOperation } from './base-articles-sequence-operation';
import { ArrayUtils } from '../../util/array-utils';
import { BaseArticleOperation } from './base-article-operation';
import { BaseDynamicArticleOperation } from './base-dynamic-article-operation';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ConnectionAreaVO, MetaElementUsageIdentityVO } from '../../vo/meta-element.vo';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { ProjectModel } from '@project/model/project.model';

export class UndoResizeBusbarOperation extends UndoReplaceArticleOperation {
  protected _articleGUID: string;
  protected _oldArticleGUID: string;
  protected _moveOperation: UndoMoveArticlesOperation;
  protected id: string;
  protected givingCubes: Connection[] = [];
  protected connectedArticleUsages: ArticleUsageVO[] = [];

  constructor(
    attachOperation: UndoAttachArticlesOperation,
    detachOperation: UndoDetachArticlesOperation,
    oldArticleGUID: string,
    articleGUID: string,
    private containerService: ContainerService,
    moveOperation: UndoMoveArticlesOperation = null
  ) {
    super(attachOperation, detachOperation);
    this._articleGUID = articleGUID;
    this._oldArticleGUID = oldArticleGUID;
    this._moveOperation = moveOperation;
  }

  public async performUndo(): Promise<void> {
    const projectModel: ProjectModel = this._attachArticlesOperation.projectModel;
    let usageIds = this.getUsageIdsFromOperation(this._attachArticlesOperation);
    this.preProcess(usageIds, projectModel, this._detachArticlesOperation);
    await super.performUndo();
    usageIds = this.getUsageIdsFromOperation(this._detachArticlesOperation);
    this.postProcess(usageIds, projectModel);
    if (this._moveOperation) {
      await this._moveOperation.performUndo();
    }
    this.cleanup();
  }

  public async performRedo(): Promise<void> {
    const projectModel: ProjectModel = this._attachArticlesOperation.projectModel;
    let usageIds = this.getUsageIdsFromOperation(this._detachArticlesOperation);
    this.preProcess(usageIds, projectModel, this._attachArticlesOperation);
    await super.performRedo();
    usageIds = this.getUsageIdsFromOperation(this._attachArticlesOperation);
    this.postProcess(usageIds, projectModel);
    if (this._moveOperation) {
      await this._moveOperation.performRedo();
    }
    this.cleanup();
  }


  private getUsageIdsFromOperation(operation: BaseArticlesSequenceOperation): any {
    const usageIds = [];
    ArrayUtils.mergeArrays(usageIds, this.extractArticleUsageIds(operation.dynamicArticleOperations));
    ArrayUtils.mergeArrays(usageIds, this.extractArticleUsageIds(operation.articleOperations));
    return usageIds;
  }

  private preProcess(usageIds: any, mainModel: any, operation: BaseArticlesSequenceOperation): void {
    usageIds.forEach((usageId: string) => {
      const articleUsage: ArticleUsageVO = mainModel.getArticleUsage(usageId);
      if (!articleUsage) {
        return;
      }
      this.givingCubes = this.getGivingCubesByBusbarArticleUsage(articleUsage);
      const connectedUsageIds: any = ArticleUsageUtils.getAcceptingAreaIds(articleUsage);
      connectedUsageIds.forEach((connectedUsageId: string) => {
        this.connectedArticleUsages.push(mainModel.getArticleUsage(connectedUsageId));
        ArticleUsageUtils.removeAssociationsWithUsage(articleUsage, usageId);
      });
      this.id = usageId;
    });
    const usageIdsFromOperation: any = this.getUsageIdsFromOperation(operation);
    operation.dynamicArticleOperations.forEach((dynamicArticleOperation: BaseDynamicArticleOperation) => {
      usageIdsFromOperation.forEach((usageIdFromOperation: string) => {
        ArticleUsageUtils.replaceArticleUsageId([dynamicArticleOperation.articleUsage], this.id, usageIdFromOperation);
      });
    });
  }

  private postProcess(usageIds: any, mainModel: any): void {
    usageIds.forEach((usageId: string) => {
      const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(usageId);
      const articleUsage: ArticleUsageVO = mainModel.getArticleUsage(usageId);
      ArticleUsageUtils.replaceArticleUsageId([articleUsage], this.id, usageId);
      ArticleUsageUtils.replaceArticleUsageId(this.connectedArticleUsages, this.id, usageId);
      const connection: ConnectionAreaVO = ConnectionInfoUtils.findDefaultRangeOnLineConnection(articleContainer.article.metaElements);
      const acceptingCube = articleContainer.findFirstConnectionByName(connection.name);
      this.givingCubes.forEach((cube: any) => {
        mainModel.createUsageBetweenConnections(cube, acceptingCube);
      });
    });
  }

  private getGivingCubesByBusbarArticleUsage(articleUsage: ArticleUsageVO): any {
    const givingCubes: Connection[] = [];
    ArticleUsageUtils.forwardConnectedMetaElementUsageIdentities(articleUsage)
      .forEach((givingIdentity: MetaElementUsageIdentityVO) => {
        const container: ArticleContainer = this.containerService.findArticleContainerInGlobal(givingIdentity.articleUsageId);
        const cube = container.findFirstConnectionByName(givingIdentity.metaElementName);
        if (cube) {
          givingCubes.push(cube);
        }
      });
    return givingCubes;
  }

  private extractArticleUsageIds(articleOperations: BaseArticleOperation[]): any {
    const usageIds: string[] = [];
    for (const operation of articleOperations) {
      usageIds.push(operation.articleUsageId);
    }
    return usageIds;
  }

  private cleanup(): void {
    this.id = null;
    this.givingCubes = [];
    this.connectedArticleUsages = [];
  }
}
